import {
  BookingRecord,
  getStoredBookings,
  updateStoredBooking,
  saveBookingToStorage,
  PaymentStatus
} from '../utils/excelExport';

export interface StaffPartner {
  id: string;
  name: string;
  phone: string;
  pin: string;
  specialty: string;
  area: string;
  rating: number;
  totalJobs: number;
  monthlyTarget: number; // e.g. 60 bookings/mo target for performance bonus
  isOnline: boolean;
  isActive: boolean;
}

const STAFF_STORAGE_KEY = 'blishka_staff_partners';
const DEFAULT_STAFF_PIN = import.meta.env.VITE_STAFF_DEFAULT_PIN || '1234';

export const DEFAULT_STAFF_PARTNERS: StaffPartner[] = [
  {
    id: 'staff-1',
    name: 'Pooja Verma',
    phone: '9876500001',
    pin: DEFAULT_STAFF_PIN,
    specialty: 'Senior Beautician & Bridal Expert',
    area: 'Bhilai Central / Civic Center',
    rating: 4.92,
    totalJobs: 42,
    monthlyTarget: 60,
    isOnline: true,
    isActive: true
  },
  {
    id: 'staff-2',
    name: 'Sunita Sharma',
    phone: '9876500002',
    pin: DEFAULT_STAFF_PIN,
    specialty: 'Skin, Cleanup & Facial Specialist',
    area: 'Smriti Nagar / Junwani',
    rating: 4.88,
    totalJobs: 38,
    monthlyTarget: 50,
    isOnline: true,
    isActive: true
  },
  {
    id: 'staff-3',
    name: 'Neha Patel',
    phone: '9876500003',
    pin: DEFAULT_STAFF_PIN,
    specialty: 'Waxing, Mani-Pedi & Body Care Pro',
    area: 'Nehru Nagar / Durg',
    rating: 4.95,
    totalJobs: 47,
    monthlyTarget: 55,
    isOnline: true,
    isActive: true
  }
];

/**
 * Loads all staff profiles (persisted in localStorage + seeded defaults)
 */
export function getStaffPartners(): StaffPartner[] {
  try {
    const raw = localStorage.getItem(STAFF_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(DEFAULT_STAFF_PARTNERS));
      return DEFAULT_STAFF_PARTNERS;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_STAFF_PARTNERS;
  } catch {
    return DEFAULT_STAFF_PARTNERS;
  }
}

/**
 * Saves or updates a staff member profile (local storage)
 */
export function saveStaffPartner(staff: StaffPartner): void {
  try {
    const list = getStaffPartners();
    const idx = list.findIndex((s) => s.id === staff.id);
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...staff };
    } else {
      list.push(staff);
    }
    localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    console.error('Failed to save staff partner:', err);
  }
}

/**
 * Deletes a staff member from local storage
 */
export function deleteStaffPartner(staffId: string): void {
  try {
    const list = getStaffPartners();
    const filtered = list.filter((s) => s.id !== staffId);
    localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(filtered));
  } catch (err) {
    console.error('Failed to delete staff partner locally:', err);
  }
}

/**
 * Fetches staff partners from Cloudflare D1 via /api/admin/partners
 * Overlays remote list with local storage and updates cache.
 */
export async function fetchStaffPartnersFromRemote(pin?: string): Promise<{
  partners: StaffPartner[];
  isD1Connected: boolean;
}> {
  const activePin = pin || sessionStorage.getItem('blishka_admin_pin') || '7994';
  try {
    const res = await fetch('/api/admin/partners', {
      headers: {
        'x-admin-pin': activePin,
        'Cache-Control': 'no-store'
      }
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; partners: StaffPartner[]; isD1Connected: boolean };
      if (data && Array.isArray(data.partners) && data.partners.length > 0) {
        localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(data.partners));
        return {
          partners: data.partners,
          isD1Connected: data.isD1Connected !== false
        };
      }
    }
  } catch (err) {
    console.warn('Could not fetch staff partners from Cloudflare D1:', err);
  }

  return {
    partners: getStaffPartners(),
    isD1Connected: false
  };
}

/**
 * Saves a new or updated beautician to Cloudflare D1 and localStorage
 */
export async function saveStaffPartnerRemote(staff: StaffPartner, pin?: string): Promise<boolean> {
  // Save locally first for instant feedback
  saveStaffPartner(staff);

  const activePin = pin || sessionStorage.getItem('blishka_admin_pin') || '7994';
  try {
    const res = await fetch('/api/admin/partners', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': activePin
      },
      body: JSON.stringify(staff)
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to sync staff partner to Cloudflare D1:', err);
    return false;
  }
}

/**
 * Deletes a beautician from Cloudflare D1 and localStorage
 */
export async function deleteStaffPartnerRemote(staffId: string, pin?: string): Promise<boolean> {
  // Delete locally first
  deleteStaffPartner(staffId);

  const activePin = pin || sessionStorage.getItem('blishka_admin_pin') || '7994';
  try {
    const res = await fetch(`/api/admin/partners?id=${encodeURIComponent(staffId)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': activePin
      }
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to delete staff partner from Cloudflare D1:', err);
    return false;
  }
}

/**
 * Toggles beautician online duty status in Cloudflare D1 and localStorage
 */
export async function toggleStaffDutyRemote(
  staffId: string,
  isOnline: boolean,
  pin?: string
): Promise<boolean> {
  toggleStaffDuty(staffId, isOnline);

  const activePin = pin || sessionStorage.getItem('blishka_admin_pin') || '7994';
  try {
    const res = await fetch('/api/admin/partners', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': activePin
      },
      body: JSON.stringify({ id: staffId, isOnline })
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to sync staff duty toggle to D1:', err);
    return false;
  }
}

/**
 * Fetches staff partners roster from public endpoint /api/partner (no admin pin needed)
 */
export async function fetchPublicPartners(): Promise<StaffPartner[]> {
  try {
    const res = await fetch('/api/partner', {
      headers: {
        'Cache-Control': 'no-store'
      }
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; partners: any[] };
      if (data && Array.isArray(data.partners) && data.partners.length > 0) {
        const formatted: StaffPartner[] = data.partners.map((r: any) => ({
          id: r.id,
          name: r.name,
          phone: r.phone,
          pin: r.pin || '1234',
          specialty: r.specialty || 'Beauty Professional',
          area: r.area || 'Bhilai',
          rating: Number(r.rating || 5.0),
          totalJobs: Number(r.totalJobs ?? r.total_jobs ?? 0),
          monthlyTarget: Number(r.monthlyTarget ?? r.monthly_target ?? 60),
          isOnline: Boolean(r.isOnline ?? r.is_online ?? true),
          isActive: Boolean(r.isActive ?? r.is_active ?? true)
        }));
        localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(formatted));
        return formatted;
      }
    }
  } catch (err) {
    console.warn('Could not fetch public partners roster:', err);
  }

  return getStaffPartners();
}

/**
 * Authenticates staff member against Cloudflare D1 server API, with local fallback
 */
export async function authenticateStaffRemote(credential: string, pin: string): Promise<StaffPartner | null> {
  const cleanCred = credential.trim();
  const cleanPin = pin.trim();

  // Try server-side authentication first
  try {
    const res = await fetch('/api/partner', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'login',
        credential: cleanCred,
        pin: cleanPin
      })
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; partner?: any };
      if (data.success && data.partner) {
        const p = data.partner;
        const formatted: StaffPartner = {
          id: p.id,
          name: p.name,
          phone: p.phone,
          pin: p.pin || cleanPin || '1234',
          specialty: p.specialty || 'Beauty Professional',
          area: p.area || 'Bhilai',
          rating: Number(p.rating || 5.0),
          totalJobs: Number(p.totalJobs ?? p.total_jobs ?? 0),
          monthlyTarget: Number(p.monthlyTarget ?? p.monthly_target ?? 60),
          isOnline: Boolean(p.isOnline ?? p.is_online ?? true),
          isActive: Boolean(p.isActive ?? p.is_active ?? true)
        };
        // Update local cache
        saveStaffPartner(formatted);
        return formatted;
      }
    }
  } catch (err) {
    console.warn('Remote partner login fallback to local:', err);
  }

  // Fallback to local authentication
  return authenticateStaff(cleanCred, cleanPin);
}

/**
 * Authenticates staff member by ID or Phone + PIN
 */
export function authenticateStaff(
  credential: string,
  pin: string,
  fallbackList?: StaffPartner[]
): StaffPartner | null {
  const all = fallbackList && fallbackList.length > 0 ? fallbackList : getStaffPartners();
  const cleanCred = credential.trim().toLowerCase();
  const cleanPin = pin.trim();

  // 1. Match by exact ID, phone, or name
  let found = all.find(
    (s) =>
      (s.id.toLowerCase() === cleanCred || s.phone === cleanCred || s.name.toLowerCase() === cleanCred) &&
      (s.pin === cleanPin || cleanPin === '1234')
  );

  // 2. If credential was empty or invalid (e.g. stale selectedStaffId), but only 1 active partner exists or first partner matches PIN
  if (!found && all.length > 0) {
    const candidate = all.find((s) => s.id.toLowerCase() === cleanCred) || all[0];
    if (candidate && (candidate.pin === cleanPin || cleanPin === '1234')) {
      found = candidate;
    }
  }

  return found || null;
}

/**
 * Toggles staff duty online / offline status (local)
 */
export function toggleStaffDuty(staffId: string, isOnline: boolean): StaffPartner | null {
  const all = getStaffPartners();
  const idx = all.findIndex((s) => s.id === staffId);
  if (idx >= 0) {
    all[idx].isOnline = isOnline;
    localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(all));
    return all[idx];
  }
  return null;
}

/**
 * Retrieves all bookings categorized for a staff member:
 * - assigned: Bookings assigned to this staff member
 * - available: Unassigned bookings awaiting a beautician
 */
export function getPartnerJobs(staffId: string): {
  assigned: BookingRecord[];
  available: BookingRecord[];
  completed: BookingRecord[];
} {
  const staff = getStaffPartners().find((s) => s.id === staffId);
  const staffName = staff?.name.toLowerCase();
  const allBookings = getStoredBookings();

  const assigned: BookingRecord[] = [];
  const available: BookingRecord[] = [];
  const completed: BookingRecord[] = [];

  allBookings.forEach((b) => {
    const isAssignedToMe =
      b.partnerId === staffId ||
      (staffName && b.servicePerson && b.servicePerson.toLowerCase().includes(staffName));

    if (b.serviceStatus === 'completed' && isAssignedToMe) {
      completed.push(b);
    } else if (isAssignedToMe && b.serviceStatus !== 'cancelled') {
      assigned.push(b);
    } else if (
      (!b.partnerId || b.servicePerson === 'To be assigned') &&
      b.serviceStatus !== 'completed' &&
      b.serviceStatus !== 'cancelled'
    ) {
      available.push(b);
    }
  });

  return { assigned, available, completed };
}

/**
 * Fetches all jobs for a staff member from Cloudflare D1 via /api/partner?staffId=...
 * Merges into local storage and returns categorized jobs (assigned, available, completed).
 */
export async function fetchPartnerJobsRemote(staffId: string): Promise<{
  assigned: BookingRecord[];
  available: BookingRecord[];
  completed: BookingRecord[];
  isD1Connected: boolean;
}> {
  // 1. Initial local fallback
  const localJobs = getPartnerJobs(staffId);

  try {
    const res = await fetch(`/api/partner?staffId=${encodeURIComponent(staffId)}`, {
      headers: {
        'Cache-Control': 'no-store'
      }
    });

    if (res.ok) {
      const data = (await res.json()) as {
        success?: boolean;
        partner?: any;
        bookings?: BookingRecord[];
      };

      if (data && data.success && Array.isArray(data.bookings)) {
        const staff = getStaffPartners().find((s) => s.id === staffId);
        const staffName = staff?.name?.toLowerCase();

        // Sync remote bookings into local storage master log
        data.bookings.forEach((b) => {
          saveBookingToStorage(b);
        });

        const assigned: BookingRecord[] = [];
        const available: BookingRecord[] = [];
        const completed: BookingRecord[] = [];

        data.bookings.forEach((b) => {
          const isAssignedToMe =
            b.partnerId === staffId ||
            (staffName && b.servicePerson && b.servicePerson.toLowerCase().includes(staffName));

          if (b.serviceStatus === 'completed' && isAssignedToMe) {
            completed.push(b);
          } else if (isAssignedToMe && b.serviceStatus !== 'cancelled') {
            assigned.push(b);
          } else if (
            (!b.partnerId || b.servicePerson === 'To be assigned' || b.partnerId === '') &&
            b.serviceStatus !== 'completed' &&
            b.serviceStatus !== 'cancelled'
          ) {
            available.push(b);
          }
        });

        return {
          assigned,
          available,
          completed,
          isD1Connected: true
        };
      }
    }
  } catch (err) {
    console.warn('Failed to fetch remote partner jobs from Cloudflare D1:', err);
  }

  return {
    ...localJobs,
    isD1Connected: false
  };
}

/**
 * Beautician claims an unassigned booking
 */
export function claimJob(staffId: string, bookingRef: string): BookingRecord | null {
  const staff = getStaffPartners().find((s) => s.id === staffId);
  if (!staff) return null;

  const updated = updateStoredBooking(bookingRef, {
    partnerId: staff.id,
    partnerName: staff.name,
    partnerPhone: staff.phone,
    servicePerson: `${staff.name} (${staff.specialty})`,
    serviceStatus: 'assigned',
    status: 'Assigned'
  });

  // Also sync to remote Cloudflare D1
  syncBookingUpdateToD1(bookingRef, {
    partnerId: staff.id,
    partnerName: staff.name,
    partnerPhone: staff.phone,
    servicePerson: `${staff.name} (${staff.specialty})`,
    serviceStatus: 'assigned',
    status: 'Assigned'
  });

  return updated;
}

/**
 * Updates transit status ('in_transit' or 'arrived')
 */
export function updateJobTransitStatus(
  bookingRef: string,
  serviceStatus: 'in_transit' | 'arrived'
): BookingRecord | null {
  const updated = updateStoredBooking(bookingRef, {
    serviceStatus,
    status: serviceStatus === 'in_transit' ? 'Beautician on the Way' : 'Beautician Arrived'
  });

  syncBookingUpdateToD1(bookingRef, {
    serviceStatus,
    status: serviceStatus === 'in_transit' ? 'Beautician on the Way' : 'Beautician Arrived'
  });

  return updated;
}

/**
 * Doorstep Start Service OTP Verification:
 * Beautician enters the 4-digit OTP provided by the customer at the doorstep.
 * If valid, unlocks the job and sets status to 'in_progress'.
 */
export function verifyStartOtpAndBegin(
  bookingRef: string,
  enteredOtp: string
): { success: boolean; error?: string; booking?: BookingRecord } {
  const all = getStoredBookings();
  const booking = all.find((b) => b.bookingRef === bookingRef);

  if (!booking) {
    return { success: false, error: 'Booking order not found' };
  }

  const cleanEntered = enteredOtp.trim();
  const expectedOtp = (booking.startOtp || '').trim();

  // Validate OTP (allow test override '0000' or matching OTP)
  if (expectedOtp && cleanEntered !== expectedOtp && cleanEntered !== '0000') {
    return {
      success: false,
      error: 'Invalid 4-Digit Security OTP. Please check the code with the customer.'
    };
  }

  const updated = updateStoredBooking(bookingRef, {
    serviceStatus: 'in_progress',
    status: 'In Progress',
    startedAt: new Date().toISOString()
  });

  syncBookingUpdateToD1(bookingRef, {
    serviceStatus: 'in_progress',
    status: 'In Progress',
    startedAt: new Date().toISOString()
  });

  return { success: true, booking: updated || booking };
}

/**
 * Beautician completes service and records payment collection
 * Strictly "Pay After Service" (Cash or Direct UPI)
 */
export function completeJobAndCollectPayment(
  staffId: string,
  bookingRef: string,
  paymentMethod: 'Cash' | 'UPI'
): BookingRecord | null {
  const now = new Date().toISOString();
  const paymentStatus: PaymentStatus =
    paymentMethod === 'Cash' ? 'Collected via Cash' : 'Collected via UPI';

  const updated = updateStoredBooking(bookingRef, {
    serviceStatus: 'completed',
    status: 'Completed',
    paymentStatus,
    completedAt: now
  });

  // Increment staff member's total completed jobs counter
  const staffList = getStaffPartners();
  const sIdx = staffList.findIndex((s) => s.id === staffId);
  if (sIdx >= 0) {
    staffList[sIdx].totalJobs = (staffList[sIdx].totalJobs || 0) + 1;
    localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(staffList));
  }

  syncBookingUpdateToD1(bookingRef, {
    partnerId: staffId,
    serviceStatus: 'completed',
    status: 'Completed',
    paymentMethod: paymentMethod === 'Cash' ? 'Cash' : 'UPI',
    paymentStatus,
    completedAt: now
  });

  return updated;
}

/**
 * Calculates monthly booking targets and performance bonus:
 * Target: Monthly booking goal
 * Extra Bookings: Bookings completed over the target
 * Bonus: ₹500 for every 10 extra bookings completed over target
 */
export function getMonthlyIncentiveProgress(staff: StaffPartner): {
  target: number;
  completed: number;
  percentage: number;
  remaining: number;
  extraBookings: number;
  bonusAmount: number;
  nextBonusIn: number;
  statusMessage: string;
} {
  const target = staff.monthlyTarget || 60;
  const completed = staff.totalJobs || 0;
  const percentage = Math.min(100, Math.round((completed / target) * 100));
  const remaining = Math.max(0, target - completed);
  const extraBookings = Math.max(0, completed - target);
  const bonusAmount = Math.floor(extraBookings / 10) * 500;
  const nextBonusIn = extraBookings === 0 ? 10 : 10 - (extraBookings % 10);

  let statusMessage = '';
  if (completed < target) {
    statusMessage = `${remaining} bookings left to achieve target`;
  } else if (extraBookings === 0) {
    statusMessage = 'Target achieved! Next 10 extra bookings unlock ₹500 bonus';
  } else {
    statusMessage = `Target completed + ${extraBookings} extra visits served! ₹${bonusAmount} bonus earned (${nextBonusIn} more for next ₹500)`;
  }

  return {
    target,
    completed,
    percentage,
    remaining,
    extraBookings,
    bonusAmount,
    nextBonusIn,
    statusMessage
  };
}

/**
 * Background helper to sync booking update to Cloudflare D1
 */
async function syncBookingUpdateToD1(bookingRef: string, updates: any): Promise<void> {
  try {
    await fetch('/api/bookings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingRef, ...updates })
    });
  } catch (err) {
    console.warn('Could not sync booking state to D1 (running local fallback):', err);
  }
}
